import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, CheckCircle, Smartphone, ChevronLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ServicesSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <section id="pricing" ref={ref} className="py-16 sm:py-28 relative noise-overlay" style={{ background: 'linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)' }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10">
        {/* Pricing Banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center py-8 sm:py-12 px-6 sm:px-8 mb-14 sm:mb-24 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.06) 0%, rgba(96, 165, 250, 0.02) 100%)',
            borderRadius: '1.5rem',
            border: '1px solid rgba(96, 165, 250, 0.1)',
          }}
        >
          <div className="relative z-10">
            <h3 className="text-xl sm:text-3xl font-black" style={{ color: '#c9a84c', textShadow: '0 2px 20px rgba(201, 168, 76, 0.5)' }}>
              {t('services.title')}
            </h3>
            <p className="mt-2 sm:mt-3 text-sm sm:text-base" style={{ color: '#e8d48b', fontWeight: 'bold' }}>{t('services.subtitle')}</p>
          </div>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="text-center mb-12 sm:mb-20"
        >
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white mb-3 sm:mb-4">
            {t('services.professionalService')} <span className="gold-text-shimmer">{t('services.priceRespect')}</span>
          </h2>
          <p className="text-blue-100 text-sm sm:text-lg max-w-2xl mx-auto px-2">
            {t('services.agreedPrice')}
          </p>
        </motion.div>

        {/* Content */}
        <div className="grid lg:grid-cols-12 gap-6 sm:gap-8 items-start">
          {/* Included features */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div
              className="relative p-6 sm:p-10 overflow-hidden"
              style={{
                background: 'linear-gradient(160deg, rgba(30, 64, 175, 0.3) 0%, rgba(96, 165, 250, 0.04) 100%)',
                borderRadius: '1.5rem',
                border: '1px solid rgba(255, 255, 255, 0.04)',
              }}
            >
              <div
                className="hidden sm:block absolute top-8 bottom-8 right-0 w-[3px]"
                style={{ background: 'linear-gradient(180deg, #c9a84c, rgba(96, 165, 250, 0.1))', borderRadius: '2px' }}
              />

              <h3 className="text-xl sm:text-2xl font-black text-white mb-1 sm:mb-2 sm:pr-4">{t('services.alwaysIncluded')}</h3>
              <p className="text-blue-200 text-xs sm:text-sm mb-6 sm:mb-8 sm:pr-4">{t('services.includedSubtitle')}</p>

              <div className="space-y-4 sm:space-y-6 sm:pr-4">
                {t('services.includedFeatures').map((feature, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -15 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.4 + i * 0.06, duration: 0.4 }}
                    className="flex items-center gap-3 sm:gap-4"
                  >
                    <div
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(74, 222, 128, 0.08)', border: '1px solid rgba(74, 222, 128, 0.15)' }}
                    >
                      <CheckCircle size={14} style={{ color: '#4ade80' }} />
                    </div>
                    <span className="text-blue-50 text-xs sm:text-sm font-medium">{feature}</span>
                  </motion.div>
                ))}
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className="w-full flex items-center justify-center gap-2 py-3.5 sm:py-4 mt-8 sm:mt-10 text-sm sm:text-base font-bold text-white relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #c9a84c 0%, #e8d48b 50%, #c9a84c 100%)',
                  boxShadow: '0 6px 25px rgba(201, 168, 76, 0.3)',
                  borderRadius: '1rem',
                  color: '#0b1a32',
                }}
                onClick={() => window.open('tel:0532222342')}
              >
                <div
                  className="absolute inset-0 opacity-30"
                  style={{
                    background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.5) 50%, transparent 60%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 3s ease-in-out infinite',
                  }}
                />
                <Phone size={18} className="relative z-10" />
                <span className="relative z-10">{t('services.callButton')}</span>
              </motion.button>
            </div>
          </motion.div>

          {/* Repair types */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="grid gap-3 sm:gap-4">
              {t('services.repairTypes').map((type, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.5 + index * 0.08, duration: 0.5 }}
                >
                  <div
                    className="flex items-center gap-4 sm:gap-6 p-5 sm:p-7 transition-all duration-400"
                    style={{
                      background: 'rgba(14, 30, 56, 0.4)',
                      border: '1px solid rgba(96, 165, 250, 0.15)',
                      borderRadius: '1.2rem',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.3)';
                      e.currentTarget.style.background = 'rgba(30, 58, 138, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.15)';
                      e.currentTarget.style.background = 'rgba(14, 30, 56, 0.4)';
                    }}
                  >
                    <div
                      className="w-11 h-11 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{
                        background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.15), rgba(96, 165, 250, 0.05))',
                        border: '1px solid rgba(96, 165, 250, 0.2)',
                      }}
                    >
                      <Smartphone size={22} style={{ color: '#60a5fa' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-bold text-white mb-1">{type.title}</h3>
                      <p className="text-blue-100 text-xs sm:text-sm leading-relaxed">{type.description}</p>
                    </div>
                    <ChevronLeft size={20} className="text-blue-300 flex-shrink-0" />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-16 sm:h-24" style={{ background: 'linear-gradient(to top, #1e40af, transparent)' }} />
    </section>
  );
};

export default ServicesSection;
